/// <amd-module name="VSS/Platform/TokenProvider" />
import { IContributedTokenProviderService } from "./Authentication.types";
import { IAuthorizationTokenProvider, IVssPageContext, IVssService, ServiceOptions, Services, VssService } from "./Context";
import { IVssContributionService } from "./Contribution";

export interface ITokenProviderService extends IVssService {
    getDefaultProvider(): IAuthorizationTokenProvider | undefined;
    getPopupProvider(): IAuthorizationTokenProvider | undefined;
    getRedirectProvider(targetLocation?: string): IAuthorizationTokenProvider | undefined;
    getSilentProvider(): IAuthorizationTokenProvider | undefined;
    clearCache(): Promise<void>;
    logout(): Promise<void>;
}

class TokenProviderService extends VssService implements ITokenProviderService {
    private _implementationService: IContributedTokenProviderService | undefined;

    _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const serviceContributions = contributionService?.getContributionChildren("ms.vss-web.token-provider-service-root");
        if (serviceContributions && serviceContributions.length > 0) {
            // we only support one token provider. ignore the rest
            this._implementationService = contributionService.getServiceSync<IContributedTokenProviderService>(serviceContributions[0]);
        }

        if (!this._implementationService) {
            setTimeout(() => document.dispatchEvent(new CustomEvent("authenticationInitialized", {})));
        }
    }

    getDefaultProvider(): IAuthorizationTokenProvider | undefined {
        if (this._implementationService) {
            return this._implementationService.getDefaultProvider();
        }
    }

    getPopupProvider(): IAuthorizationTokenProvider | undefined {
        if (this._implementationService) {
            return this._implementationService.getPopupProvider();
        }
    }

    getRedirectProvider(targetLocation?: string): IAuthorizationTokenProvider | undefined {
        if (this._implementationService) {
            return this._implementationService.getRedirectProvider(targetLocation);
        }
    }

    getSilentProvider(): IAuthorizationTokenProvider | undefined {
        if (this._implementationService) {
            return this._implementationService.getSilentProvider();
        }
    }

    clearCache(): Promise<void> {
        if (this._implementationService) {
            return this._implementationService.clearCache();
        }
        else {
            return Promise.resolve();
        }
    }

    logout(): Promise<void> {
        if (this._implementationService) {
            return this._implementationService.logout();
        }
        else {
            return Promise.resolve();
        }
    }
}

Services.add("ITokenProviderService", { serviceFactory: TokenProviderService, options: ServiceOptions.Startup | ServiceOptions.Persistant });

export function executeWithSilentAuth(pageContext: IVssPageContext, impl: (authTokenProvider?: IAuthorizationTokenProvider) => void): void {
    const tokenService = pageContext.getService<TokenProviderService>("ITokenProviderService");
    const tokenProvider = tokenService.getSilentProvider();
    if (!tokenProvider) {
        // no contributed token provider. just run the code
        impl();
    }
    else {
        tokenProvider.getAuthorizationHeader().then((authHeader: string) => {
            if (authHeader) {
                const authTokenProvider = {
                    getAuthorizationHeader: (forceRefresh?: boolean): Promise<string> => {
                        if (forceRefresh) {
                            // we're silent so we can't get new tokens, but we should still clear the cache so the next interactive call can do it
                            return tokenService.clearCache()
                                .then(() => "")
                                .catch((error) => {
                                    console.log(error);
                                    return "";
                                });
                        }
                        return Promise.resolve(authHeader);
                    },
                    providerName: "SilentTokenProvider"
                };

                impl(authTokenProvider);
            }
            else {
                // we are using a contributed token provider, but there is no cached token
                // and since we are a "silent" background process, we don't want to generate any events
                // instead, we will wait until a cached token becomes available
            }
        }).catch((error: any) => {
            console.log(error);
        });
    }
}
